import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, Users, Check, Calendar, Heart, MapPin } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_9d2782f9-5276-4e92-beba-9f63a63d9a7f/artifacts/ab2mtmab_Screenshot_20260413_174200_Chrome.jpg",
};

const benefits = [
  {
    title: "Gently Held & Supported",
    description: "Float in a soft hammock, allowing your body to feel completely supported as you experience weightlessness and total surrender.",
  },
  {
    title: "Sound Journey",
    description: "Be guided through a soothing sound journey using healing instruments, designed to calm the nervous system and bring you into a deeply restorative state.",
  },
  {
    title: "Reiki & Gentle Touch",
    description: "Throughout the session, Reiki energy healing and intuitive, gentle touch may be offered, creating a sense of safety, connection, and deep care.",
  },
  {
    title: "Feminine, Nurturing Energy",
    description: "This experience embodies soft, feminine, nurturing energy — allowing you to fully let go, receive, and be held.",
  },
];

export default function SwayWithSoundPage() {
  const [swayService, setSwayService] = useState(null);
  const [upcomingSlots, setUpcomingSlots] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const servicesRes = await axios.get(`${API}/services?category=sway`);
        if (servicesRes.data.length > 0) {
          setSwayService(servicesRes.data[0]);
          
          const slotsRes = await axios.get(`${API}/timeslots?service_id=${servicesRes.data[0].id}`);
          const sorted = slotsRes.data
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .slice(0, 6);
          setUpcomingSlots(sorted);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  };

  return (
    <div data-testid="sway-with-sound-page">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-end" data-testid="sway-hero">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.hero}
            alt="Sway with Sound"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        </div>

        <div className="container-custom relative z-10 pb-24 pt-40">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
              🌙 Group Experience
            </p>
            <h1 className="heading-xl mb-6 text-foreground">
              Sway with<br />
              <em className="italic">Sound</em>
            </h1>
            <p className="body-text text-muted-foreground max-w-lg">
              A deeply immersive and nurturing group experience designed to relax 
              the body, calm the mind, and restore your energy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* About the Experience */}
      <section className="section-spacing bg-muted" data-testid="about-experience">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
                The Experience
              </p>
              <h2 className="heading-lg mb-8 text-foreground">
                Float, Surrender,<br />
                <em className="italic">Be Held</em>
              </h2>
              <div className="space-y-6 body-text text-muted-foreground">
                <p>
                  You'll be gently held in a soft hammock, allowing your body to feel 
                  completely supported as you float effortlessly, creating a sense of 
                  weightlessness and total surrender.
                </p>
                <p>
                  As you relax into the hammock, you'll be guided through a soothing 
                  sound journey using healing instruments, designed to calm the nervous 
                  system and bring you into a deeply restorative state.
                </p>
                <p>
                  Throughout the session, Reiki energy healing and intuitive, gentle 
                  touch may be offered, creating a sense of safety, connection, and 
                  deep care. This experience embodies soft, feminine, nurturing energy 
                  — allowing you to fully let go, receive, and be held.
                </p>
                <p>
                  The combination of floating, sound, and energy work creates a powerful 
                  reset for the body and mind, leaving you feeling grounded, balanced, 
                  and deeply restored.
                </p>
                <p className="font-serif text-lg text-foreground italic">
                  This is more than a sound bath — it is a full sensory, heart-opening experience.
                </p>
              </div>

              {swayService && (
                <div className="mt-10 p-6 bg-background border border-border">
                  <p className="text-sm tracking-[0.2em] uppercase text-primary mb-3">
                    💛 Pricing
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-xl text-foreground">
                      Group Session
                    </span>
                    <span className="font-serif text-2xl text-primary">
                      £{swayService.price.toFixed(0)} per person
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{swayService.duration_minutes} mins</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span>Max {swayService.max_capacity} people</span>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-square bg-accent flex items-center justify-center">
                <div className="text-center p-12">
                  <Heart className="w-12 h-12 text-primary mx-auto mb-6" />
                  <p className="font-serif text-2xl text-foreground leading-relaxed italic">
                    "Allow yourself to fully let go, receive, and be held"
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What You'll Experience */}
      <section className="section-spacing" data-testid="benefits-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
              What You'll Experience
            </p>
            <h2 className="heading-lg text-foreground">
              A Full Sensory <em className="italic">Journey</em>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="flex gap-6 p-6 bg-muted border border-border"
                data-testid={`benefit-${index}`}
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-serif text-xl mb-2 text-foreground">
                    {benefit.title}
                  </h3>
                  <p className="body-text text-muted-foreground text-sm">
                    {benefit.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Classes */}
      <section className="section-spacing bg-muted" data-testid="upcoming-classes">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
              Upcoming Sessions
            </p>
            <h2 className="heading-lg text-foreground">
              Join the Next <em className="italic">Sway</em>
            </h2>
          </motion.div>

          {upcomingSlots.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingSlots.map((slot, index) => (
                <motion.div
                  key={slot.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="bg-background p-6 border border-border"
                  data-testid={`class-slot-${index}`}
                >
                  <div className="flex items-center gap-2 mb-4 text-primary">
                    <Calendar className="w-4 h-4" />
                    <span className="text-xs tracking-[0.1em] uppercase">
                      {formatDate(slot.date)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-serif text-2xl text-foreground">
                      {slot.time}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {slot.available_spots} spots left
                    </span>
                  </div>
                  <Link to={`/booking?service=${swayService?.id}&slot=${slot.id}`}>
                    <Button
                      className="w-full rounded-none bg-primary hover:bg-primary/90 text-primary-foreground text-xs tracking-[0.15em] uppercase"
                      disabled={slot.available_spots === 0}
                      data-testid={`book-class-${index}-btn`}
                    >
                      {slot.available_spots === 0 ? "Sold Out" : "Book This Session"}
                    </Button>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="body-text text-muted-foreground mb-6">
                No upcoming sessions scheduled yet. Subscribe to our newsletter 
                to be the first to know when new dates are released.
              </p>
              <Link to="/">
                <Button
                  variant="outline"
                  className="rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground text-xs tracking-[0.15em] uppercase"
                  data-testid="subscribe-btn"
                >
                  Get Notified
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-spacing" data-testid="sway-faq">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
              Common Questions
            </p>
            <h2 className="heading-lg text-foreground">
              Frequently Asked <em className="italic">Questions</em>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto"
          >
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="what-is" className="border border-border px-6" data-testid="faq-what-is">
                <AccordionTrigger className="font-serif text-lg text-foreground hover:no-underline py-6">
                  What is Sway with Sound?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-6">
                  Sway with Sound is a deeply relaxing experience where you rest in a supportive hammock while being guided through meditation and immersed in soothing sound healing. It's designed to help you switch off, unwind, and reconnect with yourself.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="experience" className="border border-border px-6" data-testid="faq-experience">
                <AccordionTrigger className="font-serif text-lg text-foreground hover:no-underline py-6">
                  Do I need any experience?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-6">
                  Not at all. This session is suitable for everyone, whether it's your first time or you've experienced sound healing before.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="safety" className="border border-border px-6" data-testid="faq-safety">
                <AccordionTrigger className="font-serif text-lg text-foreground hover:no-underline py-6">
                  Will I be safe in the hammock?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-6">
                  Yes, absolutely. The hammocks are securely set up and designed to fully support your body, allowing you to feel safe, held, and relaxed throughout the session. They are safety tested and can hold up to 400kg.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="bring" className="border border-border px-6" data-testid="faq-bring">
                <AccordionTrigger className="font-serif text-lg text-foreground hover:no-underline py-6">
                  What should I bring?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-6">
                  Please bring a blanket and a pillow to help you feel extra cosy and supported in your hammock. You're also welcome to bring anything else that helps you feel comfortable and relaxed. Water will be provided.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="wear" className="border border-border px-6" data-testid="faq-wear">
                <AccordionTrigger className="font-serif text-lg text-foreground hover:no-underline py-6">
                  What should I wear?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-6">
                  Wear something comfortable and cosy that you can fully relax in. Think loungewear, soft layers, and anything that helps you feel at ease.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="nervous" className="border border-border px-6" data-testid="faq-nervous">
                <AccordionTrigger className="font-serif text-lg text-foreground hover:no-underline py-6">
                  What if I feel nervous?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-6">
                  That's completely normal. This is a gentle and welcoming space, and you'll be guided throughout the session so you can relax at your own pace.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="feel" className="border border-border px-6" data-testid="faq-feel">
                <AccordionTrigger className="font-serif text-lg text-foreground hover:no-underline py-6">
                  What will I feel during the session?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-6">
                  Everyone's experience is different, but most people feel deeply relaxed, calm, and peaceful. Some people even drift into a sleep-like state.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="alone" className="border border-border px-6" data-testid="faq-alone">
                <AccordionTrigger className="font-serif text-lg text-foreground hover:no-underline py-6">
                  Can I come on my own?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-6">
                  Of course. Many people come alone, and it's a beautiful opportunity to take time for yourself and reconnect inward.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="duration" className="border border-border px-6" data-testid="faq-duration">
                <AccordionTrigger className="font-serif text-lg text-foreground hover:no-underline py-6">
                  How long is the session?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-6">
                  The session lasts around 90 minutes, giving you plenty of time to fully relax, unwind, and drop into a deeper state of rest.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="benefits" className="border border-border px-6" data-testid="faq-benefits">
                <AccordionTrigger className="font-serif text-lg text-foreground hover:no-underline py-6">
                  What are the benefits?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-6">
                  Sway with Sound can help calm the nervous system, reduce stress and anxiety, improve sleep, and leave you feeling more grounded and balanced. It's a beautiful way to reset both the mind and body.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="location" className="border border-border px-6" data-testid="faq-location">
                <AccordionTrigger className="font-serif text-lg text-foreground hover:no-underline py-6">
                  Where is it held?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-6">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-foreground mb-1">Essex Pole & Tone</p>
                      <p>1A Knightsbridge Gardens</p>
                      <p>Romford</p>
                      <p>RM7 9AD</p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="parking" className="border border-border px-6" data-testid="faq-parking">
                <AccordionTrigger className="font-serif text-lg text-foreground hover:no-underline py-6">
                  Is there parking available?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-6">
                  Yes. There is free parking in Cottons Park, just a 2 minute walk away. There is also space for a limited number of cars on the drive, and paid parking available across the road.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-primary" data-testid="sway-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="heading-lg mb-6 text-primary-foreground">
              Ready to <em className="italic">Float?</em>
            </h2>
            <p className="body-text text-primary-foreground/80 mb-10">
              Book your spot and experience the magic of Sway with Sound — 
              a full sensory, heart-opening journey.
            </p>
            <Link to="/booking">
              <Button
                size="lg"
                className="rounded-none bg-background hover:bg-background/90 text-foreground px-12 py-7 text-xs tracking-[0.2em] uppercase btn-animate"
                data-testid="book-class-cta-btn"
              >
                Book Your Session
                <ArrowRight className="ml-3 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
